
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect, ReactNode } from "react";

interface DeleteClientDialogProps {
  clientId: string;
  clientName: string;
  onClientDeleted: () => void;
  trigger?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const DeleteClientDialog = ({ 
  clientId, 
  clientName, 
  onClientDeleted, 
  trigger, 
  isOpen,
  onOpenChange 
}: DeleteClientDialogProps) => {
  const { toast } = useToast();
  const [isDeleting, setIsDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Sync external and internal open state
  useEffect(() => {
    if (isOpen !== undefined) {
      setDialogOpen(isOpen);
    }
  }, [isOpen]);

  // Check if current user is admin on mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .single();
        
        if (error) {
          console.error('Error checking admin status:', error);
          return;
        }
        
        setIsAdmin(!!data);
      } catch (error) {
        console.error('Error in checkAdminStatus:', error);
      }
    };
    
    checkAdminStatus();
  }, []);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      console.log('Starting client deletion process for:', clientId);

      // Try Edge Function first
      const { data, error } = await supabase.functions.invoke('delete-client', {
        body: { clientId }
      });

      // If Edge Function fails, use fallback - COMPLETELY WIPE OUT ALL DATA
      if (error || (data && data.error)) {
        console.log('Edge function failed, using fallback delete method - wiping all data');
        
        // Delete from Supabase Auth
        try {
          await supabase.auth.admin.deleteUser(clientId);
        } catch (authError) {
          console.log('Auth deletion failed:', authError);
        }

        // Delete all client-related data
        await supabase.from('client_teams').delete().eq('client_id', clientId);
        await supabase.from('user_roles').delete().eq('user_id', clientId);
        await supabase.from('team_members').delete().eq('user_id', clientId);
        await supabase.from('schedules').delete().eq('client_id', clientId);

        // Delete the profile completely
        const { error: deleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', clientId);

        if (deleteError) {
          console.log('Error deleting profile:', deleteError);
          // If delete fails due to triggers, do soft delete as last resort
          await supabase
            .from('profiles')
            .update({ 
              email: `deleted_${clientId}@deleted.com`,
              full_name: 'Deleted Client'
            })
            .eq('id', clientId);
        }
      }

      console.log('Client deletion completed');

      toast({
        title: "Client Deleted",
        description: "The client has been successfully deleted from the system.",
      });

      // Close the dialog
      setDialogOpen(false);
      if (onOpenChange) {
        onOpenChange(false);
      }

      // Notify parent component that client was removed
      if (onClientDeleted) {
        onClientDeleted();
      }
    } catch (error: any) {
      console.error('Error in handleDelete:', error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Sync internal state with external onOpenChange handler
  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  // If we're still checking admin status or not an admin, don't render the button
  if (!isAdmin) {
    return null;
  }

  return (
    <AlertDialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        {trigger || (
          <Button 
            variant="destructive" 
            size="sm"
            className="flex items-center gap-1 opacity-90 hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Client</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this client? This will permanently remove their account and all associated data. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting..." : "Delete Client"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
